import {
  ContentEditorActive,
  getContentEditorActive,
} from '@packages/shared/states';
import {isEditingDynamicCMSContent} from '@packages/systems/ContentEditor';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {Expressions} from '@packages/systems/wfdl';
import {EElement, type ExpressionElement} from '@packages/systems/wfdl-ast';
import {isUserComponent, pageComponentType} from '@packages/utilities/platform';
import {
  selectCurrentComponentType,
  selectIsSelectedElementInsideRichText,
  selectIsUserComponentFocused,
} from '@packages/utilities/platform-selectors';
import {createSelector} from 'reselect';
import {
  selectIsCurrentItemInline,
  selectIsDataManagerModalOpen,
} from '@packages/systems/cms/client-selectors';

export const selectContentEditorStore = (state: DesignerStores) =>
  state.ContentEditorStore;

export const selectContentEditorAiData = (
  state: DesignerStores
): ContentEditorActive['aiData'] => {
  return state.ContentEditorStore.aiData;
};

export const selectSubtree = ({ContentEditorStore}: DesignerStores) =>
  ContentEditorStore.subtree;

export const selectActiveContentEditorState = createSelector(
  selectContentEditorStore,
  (contentEditorStore): ContentEditorActive | null =>
    getContentEditorActive(contentEditorStore)
);

export const selectSelection = ({ContentEditorStore}: DesignerStores) =>
  ContentEditorStore.selection;

export const selectContentEditorSlashCommandData = (state: DesignerStores) => {
  return state.ContentEditorStore.slashCommand;
};

/** True when the active Content Editor is editing dynamic (CMS-bound) rich text — Data Manager or bound RTF on canvas. */
export const selectIsEditingDynamicCMSContent = createSelector(
  selectActiveContentEditorState,
  (activeState): boolean =>
    Boolean(activeState) && isEditingDynamicCMSContent(activeState!)
);

/**
 * Returns the `isUserComponentFocused` and `hostComponentType` values that
 * rich text component validation (`validateComponentForRichText`) should use.
 *
 * When the Content Editor is editing CMS-bound content, `focusedSymbolNativeIdPath`
 * is intentionally left untouched (clearing it caused a canvas re-render flash
 * on exit), so a stale focused component must be treated as unfocused here
 * instead. This is deliberately keyed off `selectIsEditingDynamicCMSContent`
 * rather than `DesignerStore.workflow`: the Data Manager modal doesn't flip
 * the workflow to DYNAMIC_DATA, so a workflow check misses that path. Every
 * rich text validation call site (`richTextComponentInsertion.ts`,
 * `AddComponentMenu.tsx`, `SymbolsManager.tsx`) goes through this selector
 * rather than re-deriving the same override inline.
 */
export const selectRichTextValidationFocusContext = createSelector(
  selectIsEditingDynamicCMSContent,
  selectIsUserComponentFocused,
  selectCurrentComponentType,
  (isEditingCmsContent, isUserComponentFocused, hostComponentType) => ({
    isUserComponentFocused: !isEditingCmsContent && isUserComponentFocused,
    hostComponentType: isEditingCmsContent
      ? pageComponentType
      : hostComponentType,
  })
);

/**
 * Returns the component instance element currently selected inside the Content
 * Editor, or null.
 *
 * Content Editor selection can land on the instance itself, or on a Slot /
 * Slot descendant inside that instance (Navigator + canvas selection in Rich
 * Text both do this). Walk ancestors so either selection resolves to the same
 * enclosing user-component instance, which is what the Properties surfaces
 * need.
 */
export const selectCESelectedComponentInstance = createSelector(
  selectSelection,
  selectSubtree,
  (selection, subtree): ExpressionElement | null => {
    if (!selection || !subtree) return null;
    if (selection.type !== 'Node' || !selection.nodeId) return null;

    const root = EElement(subtree);
    let el = Expressions.getElementById(root, selection.nodeId);

    while (el) {
      if (isUserComponent(el.type)) return el;
      el = Expressions.getParent(root, el.id);
    }

    return null;
  }
);

/**
 * Returns true when the component instance currently subject to editing lives
 * inside a Rich Text element. This is the single signal used to gate
 * Rich Text-specific component affordances (in-context edit, Slot population
 * UI, etc.), which are unsupported for component instances in Rich Text
 * (https://webflow.atlassian.net/browse/CONT-5945).
 *
 * Two reachability cases are covered:
 * - A component instance selected on the canvas inside a static Rich Text
 *   element (`selectIsSelectedElementInsideRichText`).
 * - A component instance faux-selected via the Content Editor, whose subtree is
 *   always a Rich Text subtree — this is the only way to reach component
 *   instances in CMS-bound (dynamic) Rich Text after the non-Content-Editor
 *   selection pipeline was removed (`selectCESelectedComponentInstance`).
 */
export const selectIsActiveComponentInstanceInRichText = createSelector(
  selectCESelectedComponentInstance,
  selectIsSelectedElementInsideRichText,
  (ceSelectedComponentInstance, isSelectedElementInsideRichText): boolean =>
    ceSelectedComponentInstance !== null || isSelectedElementInsideRichText
);

// Surface: canvas, data manager, inline item (in data manager), dm modal, rich text modal
export const selectRichTextEditingSurface = createSelector(
  selectActiveContentEditorState,
  selectIsCurrentItemInline,
  selectIsDataManagerModalOpen,
  (
    activeContentEditorState,
    isCurrentItemInline,
    isDataManagerModalOpen
  ):
    | 'canvas'
    | 'data-manager'
    | 'inline-item'
    | 'dm-modal'
    | 'rich-text-modal'
    | null => {
    if (!activeContentEditorState) {
      return null;
    }

    if (activeContentEditorState.isDataManagerEditing) {
      if (isCurrentItemInline) {
        return 'inline-item';
      }

      if (isDataManagerModalOpen) {
        return 'dm-modal';
      }

      return 'data-manager';
    }

    if (activeContentEditorState.fromModal) {
      return 'rich-text-modal';
    }

    return 'canvas';
  }
);
